import { useState, useEffect, useMemo, useCallback } from 'react';
import { Accordion, Card, Spinner } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { useSelector, useDispatch } from 'react-redux';
import { isEmpty } from 'lodash';
import Select from 'react-select';
import './LicenseMaster.css';
import CustomTooltip from '../../../Components/Tooltip/CustomTooltip';
import Loader from '../../../Components/Utility/Loader/Loader';
import ConfirmationPopup from '../../../Components/ModalPopup/ConfirmationPopup';
import { toasterMsg } from '../../../Components/Toaster/Toaster';
import * as action from '../Store/SystemMasters.action';
import { useComponent } from '../../UserSetting/UsersLibrary/UserRights';

function CustomAttrSave(isLoading) {
  return isLoading ? <Spinner animation="border" /> : <i className="icon-Tick-1 f-24" />;
}

const EMPTY_LICENSE = {
  name: null,
  aliasName: '',
  isEmpty: false,
  isNew: true,
  isActive: true,
  incoterms: [],
  _id: '',
};

function LicenseMaster() {
  const dispatch = useDispatch();
  const { t } = useTranslation('common');
  const {
    SystemMastersDetails: { licensorList, licenseList, activeIncotermsList },
    UserDetails: { userInfo },
    Users: { logInRights },
  } = useSelector((state) => state);
  const { CheckActionPermission } = useComponent();

  const MasterAddEditAccess = CheckActionPermission(logInRights, 'masters', 'licence', 'addEdit');
  const MasterDeleteAccess = CheckActionPermission(logInRights, 'masters', 'licence', 'delete');
  const loggedInUserId = userInfo.userId;

  const [licenseData, setLicenseData] = useState([]);
  const [licensorSelectData, setLicensorSelectData] = useState(null);
  const [showLicense, setShowLicense] = useState(false);
  const [buttonDownLicense, setButtonDownLicense] = useState(true);
  const [addingLicenses, setAddingLicenses] = useState(false);
  const [isLicenseLoading, setIsLicenseLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showStatusConfirm, setShowStatusConfirm] = useState(false);
  const [deleteId, setDeleteId] = useState('');
  const [statusChangeId, setStatusChangeId] = useState('');
  const [isLoadingConf, setIsLoadingConf] = useState(false);
  const [nonUniqueIds, setNonUniqueIds] = useState([]);
  const [editedLicenseId, setEditedLicenseId] = useState(-1);

  const btnDisabled = useMemo(() => licenseData?.some((item) => !item.name?.trim()), [licenseData]);
  const isUnique = nonUniqueIds.length === 0;

  useEffect(() => {
    setLicenseData(addingLicenses ? [...licenseList, EMPTY_LICENSE] : licenseList);
  }, [licenseList, addingLicenses]);

  const findDuplicateIndices = useCallback((licenses) => {
    const nameMap = {};
    licenses.forEach((item, idx) => {
      const name = item?.name?.trim();
      if (!name) return;
      nameMap[name] = nameMap[name] || [];
      nameMap[name].push(idx);
    });
    return Object.values(nameMap).flatMap((indices) => (indices.length > 1 ? indices : []));
  }, []);

  const licenseCalled = useCallback(async () => {
    await dispatch(action.getLicenseDetails({ licensorId: licensorSelectData._id, loggedInUserId }));
  }, [dispatch, licensorSelectData, loggedInUserId]);

  useEffect(() => {
    if (licensorSelectData && Object.keys(licensorSelectData).length) licenseCalled();
  }, [licensorSelectData, licenseCalled]);

  useEffect(() => {
    const duplicates = findDuplicateIndices(licenseData);
    setNonUniqueIds(duplicates);
  }, [licenseData, findDuplicateIndices]);

  useEffect(() => {
    dispatch(action.getActiveIncotermsDetails({ isActive: true }));
  }, [dispatch]);

  /**
   * Updates the edited field of a license row, keyed off the input's name attribute.
   * Only the `name` field drives the required-entry flag; editing `aliasName` leaves it untouched.
   * @param {object} e - Change event from the license name or alias input.
   * @param {number} i - Index of the license row being edited.
   */
  const handleChanged = (e, i) => {
    const { name, value } = e.target;
    const updated = [...licenseData];
    updated[i] = { ...updated[i], [name]: value };
    if (name === 'name') updated[i].isEmpty = !value.trim();
    setLicenseData(updated);
  };

  const handleAdd = async () => {
    if (!showLicense) {
      setAddingLicenses(true);
      toggleLicenseSection();
      if (isEmpty(licenseData)) await dispatch(action.getLicensorDetails({ loggedInUserId }));
    } else {
      setLicenseData((prev) => [...prev, EMPTY_LICENSE]);
    }
  };

  const toggleLicenseSection = async () => {
    setButtonDownLicense(!buttonDownLicense);
    setShowLicense(buttonDownLicense);
    setIsLicenseLoading(true);
    if (buttonDownLicense && (!licensorList || licensorList.length === 0)) await dispatch(action.getLicensorDetails({ loggedInUserId }));
    setIsLicenseLoading(false);
  };

  const handleDelete = (id) => {
    if (typeof id === 'number' || id === '') {
      const updated = [...licenseData];
      updated.splice(id, 1);
      setLicenseData(updated);
    } else {
      setDeleteId(id);
      setShowDeleteConfirm(true);
    }
  };

  const handleSave = async () => {
    const validationFailed = licenseData.some((license) => {
      const incoterms = license.incoterms || [];

      const seenIds = new Set();
      return incoterms.some((term) => {
        // Get incoterm ID string (supports both object and string formats)
        const id = typeof term.incotermsId === 'object' && term.incotermsId !== null ? term.incotermsId._id : term.incotermsId;

        const isIdInvalid = !id || id === '';
        const isValueInvalid = term.value === '' || term.value === null || term.value === undefined;

        const isDuplicate = id && seenIds.has(id);
        if (id) seenIds.add(id);

        return isIdInvalid || isValueInvalid || isDuplicate;
      });
    });

    if (validationFailed) {
      toasterMsg(t('LICENSOR_MASTER.INCOTERM_ERROR'), 'danger');
      return;
    }
    setIsSaving(true);
    const payload = licenseData.map((item) => ({
      ...item,
      name: item.name?.trim(),
      aliasName: item.aliasName?.trim() ?? '',
      isEmpty: !item.name?.trim(),
    }));
    if (payload.every((item) => item.name)) {
      const params = {
        licensorId: licensorSelectData._id,
        names: JSON.stringify(payload),
        title: 'license',
      };
      await dispatch(action.saveToSystemMaster(params, t));
      setAddingLicenses(false);
      await licenseCalled();
    } else {
      setLicenseData(payload);
    }
    setIsSaving(false);
  };

  const handleStatusToggle = (id) => {
    setStatusChangeId(id);
    setShowStatusConfirm(true);
  };

  const confirmDelete = async () => {
    setIsLoadingConf(true);
    await dispatch(action.deleteFromSystemMasters({ loggedInUserId, licenseId: deleteId, title: 'license' }, t));
    setIsLoadingConf(false);
    setShowDeleteConfirm(false);
    await licenseCalled();
  };

  const confirmStatusChange = async () => {
    setIsLoadingConf(true);
    const item = licenseData.find((ele) => ele._id === statusChangeId);
    if (item) {
      const payload = {
        id: item._id,
        licensorId: item.licensorId,
        isActive: !item.isActive,
        loggedInUserId,
        title: 'license',
      };
      await dispatch(action.setActiveInactive(payload, t));
    }
    setIsLoadingConf(false);
    setShowStatusConfirm(false);
    await licenseCalled();
  };

  const activeLicensorOptions = useMemo(() => licensorList.filter((item) => item.isActive), [licensorList]);

  const handleAddIncoterms = (id) => {
    const selectedIndex = licenseData?.findIndex((item) => item._id === id);
    const updated = [...licenseData];

    const newIncoterm = {
      incotermsId: '', // Placeholder until user selects an actual incoterms ID
      value: '', // Placeholder, should be a number ≤ 100
      isActive: true, // Default true as per schema
    };

    updated[selectedIndex] = {
      ...updated[selectedIndex],
      incoterms: updated[selectedIndex]?.incoterms ? [...updated[selectedIndex].incoterms, newIncoterm] : [newIncoterm],
    };

    setLicenseData(updated);
  };

  const handleLicenseEdit = (id) => {
    setEditedLicenseId(id);
  };

  const setLicenseIncoterms = (value, id, index, key) => {
    const updated = [...licenseData];
    const licenseIndex = licenseData?.findIndex((item) => item._id === id);
    updated[licenseIndex] = {
      ...updated[licenseIndex],
      incoterms: updated[licenseIndex]?.incoterms ? updated[licenseIndex].incoterms.map((item, ind) => (ind === index ? { ...item, [key]: value } : item)) : [],
    };
    setLicenseData(updated);
  };

  const handleIncotermsStatusToggle = (id, index, key) => {
    const updated = [...licenseData];
    const licenseIndex = licenseData?.findIndex((item) => item._id === id);
    updated[licenseIndex] = {
      ...updated[licenseIndex],
      incoterms: updated[licenseIndex]?.incoterms
        ? updated[licenseIndex].incoterms.map((item, ind) => (ind === index ? { ...item, [key]: !item[key] } : item))
        : [],
    };
    setLicenseData(updated);
  };

  const handleSaveSingleLicense = async (licenseId) => {
    const singleLicenseData = licenseData?.find((item) => item._id === licenseId);
    const seenIds = new Set();
    const validationFailed = singleLicenseData?.incoterms?.some((term) => {
      const id = typeof term.incotermsId === 'object' && term.incotermsId !== null ? term.incotermsId._id : term.incotermsId;

      const isIdInvalid = !id || id === '';
      const isValueInvalid = term.value === '' || term.value === null || term.value === undefined;

      const isDuplicate = id && seenIds.has(id);
      if (id) seenIds.add(id);

      return isIdInvalid || isValueInvalid || isDuplicate;
    });

    if (validationFailed) {
      toasterMsg(t('LICENSOR_MASTER.INCOTERM_ERROR'), 'danger');
      return;
    }
    const updated = [...licenseData];
    const licenseIndex = licenseData?.findIndex((item) => item._id === licenseId);
    const payload = {
      id: licenseId,
      incoterms: updated[licenseIndex]?.incoterms,
    };
    await dispatch(action.saveIncotermsForLicense(payload, t));
    setEditedLicenseId(-1);
    licenseCalled();
  };

  const handleIncotermsDelete = (id, index) => {
    const updated = [...licenseData];
    const licenseIndex = licenseData?.findIndex((item) => item._id === id);
    updated[licenseIndex] = {
      ...updated[licenseIndex],
      incoterms: updated[licenseIndex]?.incoterms ? updated[licenseIndex].incoterms.filter((item, ind) => ind !== index) : [],
    };
    setLicenseData(updated);
  };
  return (
    <div>
      <Accordion className="mb-4 master-accordian style-two custom-attribute-master">
        <Card className="card">
          <Card.Header>
            <div className="title-container">
              <p>{t('LICENSE_MASTER.TITLE')}</p>
            </div>
            <div className="action-title-btns">
              <CustomTooltip tooltipText={buttonDownLicense ? t('CUSTOM_ATTRIBUTES.OPEN') : t('CUSTOM_ATTRIBUTES.CLOSE')}>
                <button
                  onClick={toggleLicenseSection}
                  className="btn d-inline-flex p-0"
                  type="button"
                  aria-label={buttonDownLicense ? t('CUSTOM_ATTRIBUTES.OPEN') : t('CUSTOM_ATTRIBUTES.CLOSE')}
                >
                  <i className={`icon-new-down-arrow-icon f-24${!buttonDownLicense ? ' icon-inverse' : ''}`} />
                </button>
              </CustomTooltip>
            </div>
          </Card.Header>
          {showLicense && (
            <Card.Body>
              {isLicenseLoading ? (
                <Loader />
              ) : (
                <>
                  <div className="select-container">
                    <div className="common-react-select mb-0">
                      <Select
                        options={activeLicensorOptions}
                        placeholder={t('LICENSOR_MASTER.SELECT_LICENSOR')}
                        getOptionLabel={(o) => o.name}
                        getOptionValue={(o) => o._id}
                        name="licensor"
                        value={licensorSelectData}
                        // styles={SelectBoxStyles}
                        maxMenuHeight={130}
                        className="common-select"
                        classNamePrefix="common-select"
                        onChange={setLicensorSelectData}
                      />
                    </div>
                  </div>
                  <div className="common-scrollbar flex-fill">
                    <ul>
                      {licensorSelectData &&
                        licenseData.map((item, i) => (
                          <li key={item._id || `new-${item.name}`}>
                            <div className={`edit-mode-container ${editedLicenseId === item._id ? 'open' : ''}`}>
                              <div className="list-view" style={editedLicenseId === item._id ? { display: 'none' } : { display: 'flex' }}>
                                <input
                                  name="aliasName"
                                  value={item?.aliasName ?? ''}
                                  maxLength={5}
                                  onChange={(e) => handleChanged(e, i)}
                                  disabled={!MasterAddEditAccess}
                                  className="w-50px mr-2"
                                />
                                <div className="input-container">
                                  <input
                                    placeholder={t('LICENSE_MASTER.LICENSE_PLACEHOLDER')}
                                    type="text"
                                    name="name"
                                    maxLength={30}
                                    value={item?.name ?? ''}
                                    autoFocus={!item?._id}
                                    onChange={(e) => handleChanged(e, i)}
                                    disabled={false}
                                  />
                                  {nonUniqueIds.includes(i) && <label className="text-danger p-1">{t('SYSTEM_MASTER_TABLE.THIS_ENTRY_ALREADY_EXISTS')}</label>}
                                  {item.isEmpty && (
                                    <label className="text-danger p-1">
                                      {t('LICENSE_MASTER.TITLE')} {t('SYSTEM_MASTER_TABLE.ENTRY_REQUIRED')}
                                    </label>
                                  )}
                                </div>
                                <div className="input-action d-flex">
                                  {item._id && MasterAddEditAccess && (
                                    <>
                                      <button
                                        type="button"
                                        className="btn p-0 custom-action mr-0"
                                        onClick={() => handleLicenseEdit(item._id)}
                                        aria-label="Edit"
                                      >
                                        <span className="icon-Edit-1 f-20" />
                                      </button>
                                      <CustomTooltip tooltipText={t('SYSTEM_MASTER_TABLE.ACTIVE/INACTIVE')}>
                                        <button
                                          type="button"
                                          className="btn p-0 custom-action mr-0"
                                          onClick={() => handleStatusToggle(item._id)}
                                          id={item?._id}
                                          aria-label={item?.isActive ? 'Active' : 'Inactive'}
                                        >
                                          <span className={item.isActive ? 'icon-Eye-Open' : 'icon-Eye-Closed'} />
                                        </button>
                                      </CustomTooltip>
                                    </>
                                  )}
                                  {MasterDeleteAccess && (
                                    <CustomTooltip tooltipText={t('SYSTEM_MASTER_TABLE.DELETE')}>
                                      <button type="button" className=" btn custom-action mr-0" onClick={() => handleDelete(item._id || i)} aria-label="delete">
                                        <span className="icon-Delete f-24" />
                                      </button>
                                    </CustomTooltip>
                                  )}
                                </div>
                              </div>

                              <div className="edit-mode" style={editedLicenseId === item._id ? { display: 'block' } : { display: 'none' }}>
                                <div className="d-flex align-items-center">
                                  <div className="input-container">
                                    <input
                                      placeholder={t('LICENSE_MASTER.LICENSE_PLACEHOLDER')}
                                      type="text"
                                      name="name"
                                      maxLength={30}
                                      value={item?.name ?? ''}
                                      autoFocus={!item?._id}
                                      onChange={(e) => handleChanged(e, i)}
                                      disabled
                                    />
                                  </div>
                                  <div className="input-action d-flex">
                                    <button aria-label="Add" type="button" onClick={() => handleAddIncoterms(item._id)} className="btn p-0 custom-action mr-0">
                                      <span className="icon-Add f-24" />
                                    </button>
                                    <CustomTooltip tooltipText={t('common:COMMON_LABEL.SAVE')}>
                                      <button
                                        aria-label="Save"
                                        type="button"
                                        onClick={() => handleSaveSingleLicense(item._id)}
                                        className="btn p-0 custom-action mr-0"
                                      >
                                        <span className="icon-Save-1 f-24" />
                                      </button>
                                    </CustomTooltip>
                                    <button aria-label="Close" type="button" onClick={() => setEditedLicenseId(-1)} className="btn p-0 custom-action mr-0">
                                      <span className="icon-close cursor-pointer f-20" />
                                    </button>
                                  </div>
                                </div>
                                <p className="mb-0 mt-2 text-bold">Incoterms</p>
                                {item?.incoterms?.length > 0 ? (
                                  item?.incoterms.map((incoterm, indIncoterms) => (
                                    <div key={`${item._id}-${indIncoterms}`} className="item-row">
                                      <div className="common-react-select flex-fill mb-0">
                                        <Select
                                          options={activeIncotermsList}
                                          placeholder={t('LICENSE_MASTER.SELECT_INCOTERMS')}
                                          getOptionLabel={(option) => option.name}
                                          getOptionValue={(option) => option._id}
                                          name="incoterms"
                                          menuPosition="fixed"
                                          value={incoterm?.incotermsId}
                                          maxMenuHeight={130}
                                          className="common-select"
                                          classNamePrefix="common-select"
                                          onChange={(value) => setLicenseIncoterms(value, item._id, indIncoterms, 'incotermsId')}
                                        />
                                      </div>
                                      <div className="input-per-container">
                                        <input
                                          type="text"
                                          value={incoterm?.value}
                                          onChange={(e) => setLicenseIncoterms(e.target.value, item._id, indIncoterms, 'value')}
                                          className="form-control text-percentage f-16 ml-16 mr-8"
                                          onBlur={(e) => {
                                            const value = parseFloat(e.target.value);
                                            if (value > 100) {
                                              e.target.value = 100;
                                              setLicenseIncoterms(100, item._id, indIncoterms, 'value');
                                              toasterMsg(t('LICENSE_MASTER.VALUE_CANNOT_BE_GREATER_THAN_100'), 'danger');
                                            }
                                          }}
                                        />
                                        <span className="percentage-icon">%</span>
                                      </div>
                                      <div className="input-action d-flex">
                                        <button
                                          type="button"
                                          aria-label={incoterm?.isActive ? 'Active' : 'Inactive'}
                                          onClick={() => handleIncotermsStatusToggle(item._id, indIncoterms, 'isActive')}
                                          className="btn p-0 custom-action mr-0"
                                        >
                                          <span className={`icon-${incoterm?.isActive ? 'Eye-Open' : 'Eye-Closed'}`} />
                                        </button>
                                        <button
                                          type="button"
                                          aria-label="Delete"
                                          onClick={() => handleIncotermsDelete(item._id, indIncoterms)}
                                          className="btn p-0 custom-action mr-0"
                                        >
                                          <span className="icon-Delete f-24" />
                                        </button>
                                      </div>
                                    </div>
                                  ))
                                ) : (
                                  <p className="text-center mb-0">{t('LICENSE_MASTER.NO_INCOTERMS')}</p>
                                )}
                              </div>
                            </div>
                          </li>
                        ))}
                    </ul>
                  </div>
                  <div className="license-btn-container">
                    {MasterAddEditAccess && (
                      <>
                        <CustomTooltip tooltipText={t('CUSTOM_ATTRIBUTES.ADD_TOOLTIP')}>
                          <button
                            type="button"
                            onClick={handleAdd}
                            className="btn btn-outline sm ml-auto mr-0 mt-16 p-0"
                            disabled={btnDisabled || !isUnique}
                            aria-label="Add"
                          >
                            <span className="icon-Add text-grey  " />
                          </button>
                        </CustomTooltip>
                        <div className="section-custom-save">
                          <button
                            aria-label="Save"
                            type="button"
                            className="btn p-0"
                            onClick={handleSave}
                            disabled={!licenseData.length || btnDisabled || !isUnique || nonUniqueIds?.length}
                          >
                            {CustomAttrSave(isSaving)}
                          </button>
                        </div>
                      </>
                    )}
                  </div>
                </>
              )}
            </Card.Body>
          )}
        </Card>
      </Accordion>
      <ConfirmationPopup
        onOpen={showDeleteConfirm}
        onConfirm={confirmDelete}
        title={t('LICENSE_MASTER.DELETE_CONFIRMATION.TITLE')}
        message={t('LICENSE_MASTER.DELETE_CONFIRMATION.MESSAGE')}
        toggle={() => setShowDeleteConfirm(false)}
        loading={isLoadingConf}
      />
      <ConfirmationPopup
        onOpen={showStatusConfirm}
        onConfirm={confirmStatusChange}
        title={t('LICENSE_MASTER.ACTIVE_INACTIVE_CONFIRMATION.TITLE')}
        message={t('LICENSE_MASTER.ACTIVE_INACTIVE_CONFIRMATION.MESSAGE')}
        toggle={() => setShowStatusConfirm(false)}
        loading={isLoadingConf}
      />
    </div>
  );
}

export default LicenseMaster;
